/**
 * chat-experience 06c — the agent's prompt library as the widget reads it.
 *
 * The bootstrap (06b) serves `prompts: [{ id, origin, label, prompt, collection }]`, the bound agent's
 * published library in library order, already merged server-side (12). `collection: ""` means top
 * level. The key is ABSENT (never `[]`) on every degraded path, and both spellings render nothing.
 *
 * Both surfaces — the starters list on an empty conversation and the composer's `+` → Prompts view —
 * render the rows built here (D-40), so they cannot disagree about the library.
 */
import { get } from "lodash";

/** Parent §7 step 5: the first six TOP-LEVEL prompts in library order (D-41 — order, not a flag). */
export const MAX_STARTERS = 6;

const isText = (v) => typeof v === "string" && v.trim() !== "";

/** The collection an entry belongs to; `""` (or anything that is not a non-blank string) is the top level. */
export const collectionOf = (p) => (isText(p?.collection) ? p.collection.trim() : "");

/**
 * Drop anything unrenderable (no id, a blank label or prompt) and any repeat of an id already seen, so
 * one malformed entry never blanks the block. Nothing is re-ordered, merged or filtered otherwise.
 */
export function sanitizePrompts(list) {
	if (!Array.isArray(list)) return [];
	const seen = new Set();
	return list.filter((p) => {
		if (!p || !isText(p.id) || !isText(p.label) || !isText(p.prompt) || seen.has(p.id)) return false;
		seen.add(p.id);
		return true;
	});
}

/** The bootstrap key, read in the HomeArticles style; absent, `[]` and all-malformed all give `[]`. */
export function readPrompts(widget) {
	return sanitizePrompts(get(widget, "prompts", []));
}

/** The starters: the first `MAX_STARTERS` top-level prompts. Collections are never capped away. */
export function pickStarters(list) {
	return sanitizePrompts(list).filter((p) => collectionOf(p) === "").slice(0, MAX_STARTERS);
}

/**
 * The one flat render list both surfaces draw (§5.2, D-36, D-40):
 *   - every top-level prompt first, in library order (only the first six with `{ starters: true }`);
 *   - then one row per collection, in first-appearance order, with its prompt count;
 *   - the prompts of `openCollection` straight after its row (depth 1). One level, never two.
 * A library without collections produces prompt rows only — no header, no disclosure.
 *
 * Row shapes: `{ type: "prompt", key, prompt, depth }` · `{ type: "collection", key, name, count, open }`.
 */
export function buildRows(list, openCollection = null, { starters = false } = {}) {
	const items = sanitizePrompts(list);
	const top = starters ? pickStarters(items) : items.filter((p) => collectionOf(p) === "");
	const rows = top.map((p) => ({ type: "prompt", key: `p:${p.id}`, prompt: p, depth: 0 }));

	const groups = new Map();
	for (const p of items) {
		const name = collectionOf(p);
		if (name === "") continue;
		if (!groups.has(name)) groups.set(name, []);
		groups.get(name).push(p);
	}
	for (const [name, members] of groups) {
		const open = name === openCollection;
		rows.push({ type: "collection", key: `c:${name}`, name, count: members.length, open });
		if (open) members.forEach((p) => rows.push({ type: "prompt", key: `p:${p.id}`, prompt: p, depth: 1 }));
	}
	return rows;
}
